import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY } from '../utils/constants';
import PayrollMusterUpload from './PayrollMusterUpload';
import PayrollReview from './PayrollReview';
import PayrollEmployees from './PayrollEmployees';
import { Lock, ShieldAlert, Loader2 } from 'lucide-react';

// Payroll shell: access gate → screen lock → tabs.
// Upload = the monthly MUSTER (attendance) upload + payslip generation
//   (PayrollMusterUpload). Salary components live on the employee master —
//   the old per-month salary-sheet import is retired.
// Review = what is stored for a month (PayrollReview).
// Employees = the salary master (PayrollEmployees).

// ─── Screen lock constants ────────────────────────────────────────────────────
const MAX_ATTEMPTS = 5;
const COOLDOWN_SECONDS = 60;

const card = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  padding: 20,
};

export default function Payroll({ onNavigate }) {
  // Access + lock
  const [access, setAccess] = useState('checking'); // checking | granted | denied | error
  const [userEmail, setUserEmail] = useState('');
  const [unlocked, setUnlocked] = useState(false); // React state ONLY — reload/navigation re-locks
  const [pw, setPw] = useState('');
  const [pwError, setPwError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [failCount, setFailCount] = useState(0);
  const [cooldownLeft, setCooldownLeft] = useState(0);

  // Tabs: 'upload' (muster → attendance + slips) | 'review' (what's stored) | 'employees' (salary master)
  const [tab, setTab] = useState('upload');
  const [reviewMonth, setReviewMonth] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (mounted) setUserEmail(userData?.user?.email || '');
      const { data, error } = await supabase.rpc('is_payroll_admin');
      if (!mounted) return;
      if (error) setAccess('error');
      else setAccess(data === true ? 'granted' : 'denied');
    })();
    return () => { mounted = false; };
  }, []);

  // Cool-down countdown after MAX_ATTEMPTS failures.
  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const t = setInterval(() => {
      setCooldownLeft((s) => {
        if (s <= 1) { setFailCount(0); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [cooldownLeft > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (verifying || cooldownLeft > 0 || !pw) return;
    setVerifying(true);
    setPwError('');
    try {
      // Re-verify the user's OWN password. Server-side check, no stored secret.
      const { error } = await supabase.auth.signInWithPassword({ email: userEmail, password: pw });
      if (error) {
        const fails = failCount + 1;
        setFailCount(fails);
        setPw('');
        if (fails >= MAX_ATTEMPTS) {
          setCooldownLeft(COOLDOWN_SECONDS);
          setPwError('');
        } else {
          setPwError(`Incorrect password. ${MAX_ATTEMPTS - fails} attempt${MAX_ATTEMPTS - fails === 1 ? '' : 's'} left before a ${COOLDOWN_SECONDS}-second pause.`);
        }
      } else {
        setUnlocked(true);
        setPw('');
        setFailCount(0);
      }
    } catch {
      setPwError('Could not reach the server. Check your connection and try again.');
    } finally {
      setVerifying(false);
    }
  };

  // ── Gate screens ────────────────────────────────────────────────────────────
  if (access === 'checking') {
    return (
      <div style={{ ...card, maxWidth: 420, margin: '48px auto', textAlign: 'center', color: 'var(--color-ink-muted)', fontSize: '0.85rem' }}>
        <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} aria-hidden="true" /> Checking payroll access…
      </div>
    );
  }

  if (access === 'error') {
    return (
      <div style={{ ...card, maxWidth: 420, margin: '48px auto', textAlign: 'center' }}>
        <p style={{ fontSize: '0.85rem', color: 'var(--color-danger)', marginBottom: 12 }}>Could not check payroll access. Check your connection.</p>
        <button className="btn btn-outline btn-sm" onClick={() => onNavigate('home')}>Back to Home</button>
      </div>
    );
  }

  if (access === 'denied') {
    return (
      <div style={{ ...card, maxWidth: 440, margin: '48px auto', textAlign: 'center' }}>
        <ShieldAlert size={28} style={{ color: 'var(--color-warning-strong)', marginBottom: 10 }} aria-hidden="true" />
        <h2 style={{ fontSize: '1rem', fontWeight: 700, color: NAVY, marginBottom: 6 }}>No payroll access</h2>
        <p style={{ fontSize: '0.83rem', color: 'var(--color-ink-secondary)', lineHeight: 1.5, marginBottom: 16 }}>
          Your account ({userEmail}) is not on the payroll access list. Salary data is restricted — if you need access, contact the administrator.
        </p>
        <button className="btn btn-outline btn-sm" onClick={() => onNavigate('home')}>Back to Home</button>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div style={{ ...card, maxWidth: 400, margin: '48px auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Lock size={26} style={{ color: NAVY, marginBottom: 8 }} aria-hidden="true" />
          <h2 style={{ fontSize: '1rem', fontWeight: 700, color: NAVY, marginBottom: 4 }}>Payroll is locked</h2>
          <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)' }}>
            Re-enter the password for {userEmail} to continue.
          </p>
        </div>
        <form onSubmit={handleUnlock}>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoFocus
            autoComplete="current-password"
            disabled={verifying || cooldownLeft > 0}
            placeholder="Your login password"
            style={{ width: '100%', boxSizing: 'border-box', background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '10px 12px', fontSize: '0.85rem', color: NAVY, outline: 'none', marginBottom: 10 }}
          />
          {pwError && <p className="field-error" style={{ marginBottom: 10 }}>{pwError}</p>}
          {cooldownLeft > 0 && (
            <p style={{ fontSize: '0.8rem', color: 'var(--color-warning-strong)', marginBottom: 10, lineHeight: 1.5 }}>
              Too many incorrect attempts. Wait {cooldownLeft}s before trying again — this pause protects your account from being rate-limited or locked.
            </p>
          )}
          <button type="submit" className="btn btn-navy" disabled={verifying || cooldownLeft > 0 || !pw} style={{ width: '100%' }}>
            {verifying ? 'Checking…' : cooldownLeft > 0 ? `Locked for ${cooldownLeft}s` : 'Unlock payroll'}
          </button>
        </form>
      </div>
    );
  }

  // ── Main screen ─────────────────────────────────────────────────────────────
  const tabBtn = (active) => ({
    padding: '7px 16px',
    borderRadius: 8,
    border: active ? '1.5px solid var(--color-accent)' : '1.5px solid var(--color-border-strong)',
    background: active ? 'var(--color-accent-light)' : 'var(--color-surface)',
    color: NAVY,
    fontWeight: 700,
    fontSize: '0.8rem',
    cursor: 'pointer',
  });

  return (
    <div>
      <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY, marginBottom: 4 }}>Payroll</h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', marginBottom: 16 }}>
        Upload the monthly muster (attendance) report to compute and store payslips, review what is stored for a month, or maintain
        the employee master (salary components, leave type, active status).
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button type="button" style={tabBtn(tab === 'upload')} onClick={() => setTab('upload')}>Upload</button>
        <button type="button" style={tabBtn(tab === 'review')} onClick={() => setTab('review')}>Review</button>
        <button type="button" style={tabBtn(tab === 'employees')} onClick={() => setTab('employees')}>Employees</button>
      </div>

      {tab === 'employees' ? (
        <PayrollEmployees />
      ) : tab === 'review' ? (
        <PayrollReview initialMonth={reviewMonth} />
      ) : (
        <PayrollMusterUpload
          onOpenEmployees={() => setTab('employees')}
          onReviewMonth={(m) => { setReviewMonth(m); setTab('review'); }}
        />
      )}
    </div>
  );
}
